import {
  BestPriceProductInfo,
  BestPriceProductInfoMap,
  GetBestPriceInputs,
  getBestPrices as fetchBestPrices,
} from "@/components/banners/getBestPrice";
import { AppState } from "@/store/store";
import {
  ActionReducerMapBuilder,
  Dispatch,
  GetThunkAPI,
  PayloadAction,
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { deleteCookie, getCookie, setCookie } from "cookies-next";

declare type AsyncThunkConfig = {
  state?: unknown;
  dispatch?: Dispatch;
  extra?: unknown;
  rejectValue?: unknown;
  serializedErrorType?: unknown;
  pendingMeta?: unknown;
  fulfilledMeta?: unknown;
  rejectedMeta?: unknown;
};

export type SearchSliceState = {
  bestPricesRetrievalState: "idle" | "pending";
  bestPrices: { [key: string]: BestPriceProductInfo };
  bestPriceRequests: string[];
  bestPriceCacheInfo: {
    [key: number]: BestPriceCacheInfoType;
  };
  purchasedBefore: boolean;
  freeShipping: boolean;
  fastDelivery: boolean;
  authorizedVendor: boolean;
  shippingRegionId: number;
  shippingZipCode: string;
  prevPath: string;
};
type BestPriceCacheInfoType = {
  postalCode: string;
  tag: string;
  additionalCacheKey: string;
};
const initialState: SearchSliceState = {
  bestPricesRetrievalState: "idle",
  bestPrices: {},
  bestPriceRequests: [],
  bestPriceCacheInfo: {},
  purchasedBefore: getCookie("purchasedBefore") === "true",
  freeShipping: getCookie("freeShipping") === "true",
  fastDelivery: getCookie("fastDelivery") === "true",
  authorizedVendor: getCookie("authorizedVendor") === "true",
  shippingRegionId: +(getCookie("free_shipping_region_id") || "0"),
  shippingZipCode: "",
  prevPath: "",
};

const clearSearchData = (state: SearchSliceState) => {
  state.purchasedBefore = false;
  state.fastDelivery = false;
  state.freeShipping = false;
  state.authorizedVendor = false;
  deleteCookie("purchasedBefore");
  deleteCookie("freeShipping");
  deleteCookie("fastDelivery");
  deleteCookie("authorizedVendor");
  return state;
};
export const searchSlice = createSlice({
  name: "searchSlice",
  initialState,
  reducers: {
    addBestPriceRequest: (state: SearchSliceState, action: PayloadAction<string>) => {
      state.bestPriceRequests.push(action.payload);
    },
    addBestPriceCacheInfo: (
      state: SearchSliceState,
      action: PayloadAction<{ mpId: number } & BestPriceCacheInfoType>,
    ) => {
      state.bestPriceCacheInfo[action.payload.mpId] = {
        postalCode: action.payload.postalCode,
        tag: action.payload.tag,
        additionalCacheKey: action.payload.additionalCacheKey,
      };
    },
    setPurchasedBefore: (state: SearchSliceState, action: PayloadAction<boolean>) => {
      state.purchasedBefore = action.payload;
      setCookie("purchasedBefore", action.payload ? "true" : "false");

      return state;
    },
    setFreeShipping: (state: SearchSliceState, action: PayloadAction<boolean>) => {
      state.freeShipping = action.payload;
      setCookie("freeShipping", action.payload ? "true" : "false");

      return state;
    },
    setFastDelivery: (state: SearchSliceState, action: PayloadAction<boolean>) => {
      state.fastDelivery = action.payload;
      setCookie("fastDelivery", action.payload ? "true" : "false");

      return state;
    },
    setAuthorizedVendor: (state: SearchSliceState, action: PayloadAction<boolean>) => {
      state.authorizedVendor = action.payload;
      setCookie("authorizedVendor", action.payload ? "true" : "false");

      return state;
    },
    setShippingRegionId: (state: SearchSliceState, action: PayloadAction<number | string | undefined>) => {
      state.shippingRegionId = +(action.payload || "0");
      setCookie("free_shipping_region_id", action.payload);

      return state;
    },
    setShippingZipCode: (state: SearchSliceState, action: PayloadAction<string | undefined>) => {
      state.shippingZipCode = action.payload || "";

      return state;
    },
    clearSearchData,
    setValuesFromCookies: (state: SearchSliceState, action: PayloadAction<any>) => {
      const prevPath = state.prevPath;
      const currentPath = window.location.pathname;
      state.prevPath = currentPath;
      if (prevPath && prevPath !== currentPath) {
        return clearSearchData(state);
      }
      state.purchasedBefore = getCookie("purchasedBefore") === "true";
      state.freeShipping = getCookie("freeShipping") === "true";
      state.fastDelivery = getCookie("fastDelivery") === "true";
      state.authorizedVendor = getCookie("authorizedVendor") === "true";
      state.shippingRegionId = +(getCookie("free_shipping_region_id") || "0") || action.payload;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<SearchSliceState>) => {
    builder.addCase(
      getBestPrices.fulfilled,
      (state: SearchSliceState, action: PayloadAction<BestPriceProductInfoMap | undefined>) => {
        const payload = action.payload;
        if (payload) {
          Object.entries(payload).forEach(([key, value]) => {
            state.bestPrices[parseInt(key)] = value;
          });
        }
        state.bestPricesRetrievalState = "idle";
      },
    );
    builder.addCase(getBestPrices.pending, (state: SearchSliceState) => {
      state.bestPricesRetrievalState = "pending";
    });
    builder.addCase(getBestPrices.rejected, (state: SearchSliceState) => {
      state.bestPricesRetrievalState = "idle";
      // console.error("getBestPrices.rejected was thrown: ", action);
    });
  },
});

/**
 * This factory function will create a selector that returns the best prices object from
 * the search slice for a particular mpId. This is memoized for performance.
 *
 * In order to utilize this, place the following code in your component:
 *
 *   1. const selectBestPriceByMpId = useMemo(makeGetBestPriceByMpIdSelector, []);
 *   2. const productFromServer = useAppSelector((state) => selectBestPriceByMpId(state, hit.mp_id));
 *
 * With this, your component will have a memoized version of this selector.
 * @returns a selector for the search slice's best prices object.
 */
export const makeGetBestPriceByMpIdSelector = () => {
  const selectBestPriceByMpId = createSelector(
    [(state: AppState) => state.searchSlice.bestPrices, (state: AppState, mpId: number) => mpId],
    (bestPrices: { [key: number]: BestPriceProductInfo }, mpId: number) => bestPrices[mpId],
  );
  return selectBestPriceByMpId;
};

export const actions = searchSlice.actions;
export default searchSlice.reducer;

export const getBestPrices = createAsyncThunk(
  "getBestPrices",
  async ({ additionalCacheKey = "", ...request }: GetBestPriceInputs, thunkApi: GetThunkAPI<AsyncThunkConfig>) => {
    request.mpIds.sort(); // Sorting to encourage cache hits.
    /** A copy of request.postalCode that is aligned with how it is stored in the cache info store but not how it will be sent to the server. */
    const postalCode = request.postalCode ? request.postalCode : "none";
    /** A copy of request.tag that is aligned with how it is stored in the cache info store but not how it will be sent to the server. */
    const tag = request.tag ? request.tag : "none";

    // We want to cache the request so we don't make the same request multiple times in a row
    const cacheKey = JSON.stringify({ ...request, additionalCacheKey });
    const state = thunkApi.getState() as AppState;
    if (state.searchSlice.bestPriceRequests.some((bpr: string) => bpr === cacheKey)) {
      thunkApi.abort("MpId list already requested, aborting..");
      console.log("getBestPrices Thunk | MpId list already requested, aborting..");
      return;
    }
    thunkApi.dispatch(searchSlice.actions.addBestPriceRequest(cacheKey));

    // Next, we will clear out an mpIds that are already in the bestPrices object
    const droppedMpIds: number[] = [];
    request.mpIds = request.mpIds.filter((mpId) => {
      if (
        state.searchSlice.bestPriceCacheInfo[mpId] &&
        postalCode === state.searchSlice.bestPriceCacheInfo[mpId].postalCode &&
        tag === state.searchSlice.bestPriceCacheInfo[mpId].tag &&
        additionalCacheKey === state.searchSlice.bestPriceCacheInfo[mpId].additionalCacheKey
      ) {
        droppedMpIds.push(mpId);
        return false;
      }
      thunkApi.dispatch(
        searchSlice.actions.addBestPriceCacheInfo({
          mpId,
          postalCode: postalCode,
          tag: tag,
          additionalCacheKey: additionalCacheKey,
        }),
      );
      return true;
    });
    console.log("getBestPrices Thunk | Dropping mpIds as they are already cached: ", droppedMpIds);
    console.log("getBestPrices Thunk | Requesting best prices for: ", request.mpIds);
    if (request.mpIds.length === 0) {
      thunkApi.abort("All requested mpIds are already in the store, aborting..");
      console.log("getBestPrices Thunk | All requested mpIds are already in the store, aborting..");
      return;
    }

    return await fetchBestPrices(request);
  },
);
